import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Sparkles, ExternalLink, Loader2, Trash2, Download, ChevronDown, Eye } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import anime from 'animejs';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { toast } from 'sonner';
import type { GeneratedDocument, GeneratedDocumentType, LeasingActionType, LeasingDealDTO } from '#types/types';

interface GenerateIncentiveLetterActionPanelProps {
  dealId: string;
  highlightedActionTypes?: LeasingActionType[];
  focusedActionType?: LeasingActionType;
}

type DocumentTemplateCatalogItem = {
  id: string;
  name: string;
  description: string | null;
  documentType: GeneratedDocumentType;
  isGlobal: boolean;
  createdAt: string;
  updatedAt: string;
};

type DocumentTemplateDetail = DocumentTemplateCatalogItem & {
  htmlContent: string;
  requiredDataSchema: unknown;
  mappingInstructions: string | null;
};

type DealDraftingKernelState = {
  draftingPreviewMode?: 'TEMPLATE' | 'DOCUMENT';
  draftingSelectedDocumentId?: string | null;
  draftingSelectedDocumentType?: GeneratedDocumentType;
  draftingIsGenerating?: boolean;
  draftingPendingDocumentId?: string | null;
  draftingFocusRequestAt?: number;
};

const readDealDraftingKernelState = (value: unknown): DealDraftingKernelState => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const state = value as Record<string, unknown>;
  return {
    draftingPreviewMode:
      state.draftingPreviewMode === 'TEMPLATE' || state.draftingPreviewMode === 'DOCUMENT'
        ? state.draftingPreviewMode
        : undefined,
    draftingSelectedDocumentId:
      typeof state.draftingSelectedDocumentId === 'string'
        ? state.draftingSelectedDocumentId
        : state.draftingSelectedDocumentId === null
          ? null
          : undefined,
    draftingSelectedDocumentType:
      state.draftingSelectedDocumentType === 'INCENTIVE' || state.draftingSelectedDocumentType === 'LOI'
        ? state.draftingSelectedDocumentType
        : undefined,
    draftingIsGenerating:
      typeof state.draftingIsGenerating === 'boolean' ? state.draftingIsGenerating : undefined,
    draftingPendingDocumentId:
      typeof state.draftingPendingDocumentId === 'string'
        ? state.draftingPendingDocumentId
        : state.draftingPendingDocumentId === null
          ? null
          : undefined,
    draftingFocusRequestAt:
      typeof state.draftingFocusRequestAt === 'number' && Number.isFinite(state.draftingFocusRequestAt)
        ? state.draftingFocusRequestAt
        : undefined,
  };
};

const isDocumentReady = (doc: GeneratedDocument | undefined): boolean => {
  if (!doc) return false;
  return doc.status === 'SUCCESS' || doc.status === 'COMPLETE';
};

const isDocumentStale = (doc: GeneratedDocument | undefined): boolean => {
  const staleness = doc?.metadata?.staleness;
  if (!staleness || typeof staleness !== 'object') return false;
  return staleness.isStale === true;
};

const stopEventPropagation = (event: React.MouseEvent) => event.stopPropagation();
const preventEventDefault = (event: React.MouseEvent) => event.preventDefault();

const resolveDocumentTypeForAction = (actionType?: LeasingActionType): GeneratedDocumentType | null => {
  if (actionType === 'REDACT_INCENTIVE_LETTER') return 'INCENTIVE';
  if (actionType === 'REDACT_LOI') return 'LOI';
  return null;
};

export function GenerateIncentiveLetterActionPanel({
  dealId,
  highlightedActionTypes = [],
  focusedActionType,
}: GenerateIncentiveLetterActionPanelProps) {
  const { kernel } = useKernel();
  const deal = useKernelStore((state) => state.inventory.Deal.registry[dealId]) as LeasingDealDTO | undefined;
  const rawPageState = useKernelStore((state) => state.inventory.Deal.registry[dealId] ?? null);
  const draftingKernelState = useMemo(() => readDealDraftingKernelState(rawPageState), [rawPageState]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<GeneratedDocumentType>(
    () => draftingKernelState.draftingSelectedDocumentType ?? 'INCENTIVE'
  );
  const [activePreviewMode, setActivePreviewMode] = useState<'TEMPLATE' | 'DOCUMENT'>(
    () => draftingKernelState.draftingPreviewMode ?? 'DOCUMENT'
  );
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    () => draftingKernelState.draftingSelectedDocumentId ?? null
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplateDetail | null>(null);
  const [templateCatalog, setTemplateCatalog] = useState<DocumentTemplateCatalogItem[]>([]);
  const [isTemplateCatalogLoading, setIsTemplateCatalogLoading] = useState(false);
  const [isTemplatePreviewLoading, setIsTemplatePreviewLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ docId: string; x: number; y: number } | null>(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const templatePulseRef = useRef<HTMLDivElement>(null);
  const persistDraftingUiState = useCallback(
    (patch: Partial<DealDraftingKernelState>) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('Deal', dealId, patch);
    },
    [kernel, dealId]
  );

  useEffect(() => {
    const targetDocumentType = resolveDocumentTypeForAction(focusedActionType);
    if (!targetDocumentType) return;

    const docsForType = [...(deal?.generatedDocs ?? [])]
      .filter((doc) => doc.type === targetDocumentType)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const latestDocId = docsForType[0]?.id ?? null;
    const nextPreviewMode: 'TEMPLATE' | 'DOCUMENT' = latestDocId ? 'DOCUMENT' : 'TEMPLATE';

    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setSelectedDocumentType((current) => (current === targetDocumentType ? current : targetDocumentType));
    setActivePreviewMode((current) => (current === nextPreviewMode ? current : nextPreviewMode));
    setSelectedDocumentId((current) => (current === latestDocId ? current : latestDocId));

    persistDraftingUiState({
      draftingPreviewMode: nextPreviewMode,
      draftingSelectedDocumentType: targetDocumentType,
      draftingSelectedDocumentId: latestDocId,
    });
  }, [focusedActionType, deal?.generatedDocs, persistDraftingUiState]);

  useEffect(() => {
    if (draftingKernelState.draftingSelectedDocumentType) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      setSelectedDocumentType((current) =>
        current === draftingKernelState.draftingSelectedDocumentType ? current : draftingKernelState.draftingSelectedDocumentType!
      );
    }
    if (draftingKernelState.draftingPreviewMode) {
      setActivePreviewMode((current) =>
        current === draftingKernelState.draftingPreviewMode ? current : draftingKernelState.draftingPreviewMode!
      );
    }
    if (draftingKernelState.draftingSelectedDocumentId !== undefined) {
      setSelectedDocumentId((current) =>
        current === draftingKernelState.draftingSelectedDocumentId ? current : (draftingKernelState.draftingSelectedDocumentId ?? null)
      );
    }
  }, [
    draftingKernelState.draftingFocusRequestAt,
    draftingKernelState.draftingSelectedDocumentId,
    draftingKernelState.draftingPreviewMode,
    draftingKernelState.draftingSelectedDocumentType,
  ]);

  useEffect(() => {
    if (kernel.current && dealId && !deal) {
      void kernel.current.syncEntity('Deal', dealId);
    }
  }, [kernel, dealId, deal]);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setIsTemplateCatalogLoading(true);
      try {
        const response = await fetch(`/api/inventory/document-templates?dealId=${encodeURIComponent(dealId)}`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error?.message || 'Failed to load templates');
        }
        const payload = await response.json();
        if (cancelled) return;
        const templates = Array.isArray(payload?.templates) ? payload.templates : [];
        setTemplateCatalog(templates);
      } catch (error: any) {
        if (cancelled || error?.name === 'AbortError') return;
        toast.error(error?.message || 'Failed to load templates');
        setTemplateCatalog([]);
      } finally {
        if (!cancelled) {
          setIsTemplateCatalogLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [dealId]);

  const generatedDocs = useMemo(() => {
    if (!deal?.generatedDocs || deal.generatedDocs.length === 0) return [];
    return [...deal.generatedDocs]
      .filter((doc) => doc.type === selectedDocumentType)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [deal?.generatedDocs, selectedDocumentType]);

  const pendingGeneratedDocumentId = draftingKernelState.draftingPendingDocumentId ?? null;
  const pendingGeneratedDocument = useMemo(() => {
    if (!pendingGeneratedDocumentId) return null;
    const allDocs = deal?.generatedDocs || [];
    return allDocs.find((doc) => doc.id === pendingGeneratedDocumentId) ?? null;
  }, [deal?.generatedDocs, pendingGeneratedDocumentId]);
  const isKernelGenerating = draftingKernelState.draftingIsGenerating === true;
  const isAwaitingGeneratedDocument = Boolean(
    pendingGeneratedDocumentId &&
    (!pendingGeneratedDocument || !isDocumentReady(pendingGeneratedDocument))
  );
  const isGenerationBusy = isGenerating || isKernelGenerating;

  useEffect(() => {
    if (!deal) return;

    if (generatedDocs.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      setSelectedDocumentId(null);
      if (activePreviewMode === 'DOCUMENT' && draftingKernelState.draftingPreviewMode !== 'DOCUMENT') {
        setActivePreviewMode('TEMPLATE');
      }
      return;
    }

    const latestDocId = generatedDocs[0]?.id ?? null;
    const hasSelectedDocument = Boolean(selectedDocumentId && generatedDocs.some((doc) => doc.id === selectedDocumentId));

    if (activePreviewMode === 'TEMPLATE' && !draftingKernelState.draftingPreviewMode) {
      setActivePreviewMode('DOCUMENT');
      setSelectedDocumentId(latestDocId);
      persistDraftingUiState({
        draftingPreviewMode: 'DOCUMENT',
        draftingSelectedDocumentType: selectedDocumentType,
        draftingSelectedDocumentId: latestDocId,
      });
      return;
    }

    if (activePreviewMode === 'DOCUMENT' && !hasSelectedDocument) {
      setSelectedDocumentId(latestDocId);
      if (!draftingKernelState.draftingSelectedDocumentId || draftingKernelState.draftingSelectedDocumentId !== latestDocId) {
        persistDraftingUiState({
          draftingPreviewMode: 'DOCUMENT',
          draftingSelectedDocumentType: selectedDocumentType,
          draftingSelectedDocumentId: latestDocId,
        });
      }
    }
  }, [
    deal,
    generatedDocs,
    selectedDocumentId,
    activePreviewMode,
    draftingKernelState.draftingPreviewMode,
    draftingKernelState.draftingSelectedDocumentId,
    selectedDocumentType,
    persistDraftingUiState,
  ]);

  const selectedDocument = useMemo(
    () => generatedDocs.find((doc) => doc.id === selectedDocumentId) ?? null,
    [generatedDocs, selectedDocumentId]
  );

  useEffect(() => {
    if (!kernel.current || !pendingGeneratedDocumentId || !pendingGeneratedDocument) return;
    if (!isDocumentReady(pendingGeneratedDocument)) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setSelectedDocumentType(pendingGeneratedDocument.type);
    setActivePreviewMode('DOCUMENT');
    setSelectedDocumentId(pendingGeneratedDocument.id);

    kernel.current.patchEntityRuntimeState(
      'Deal',
      dealId,
      {
        draftingPreviewMode: 'DOCUMENT',
        draftingSelectedDocumentId: pendingGeneratedDocument.id,
        draftingSelectedDocumentType: pendingGeneratedDocument.type,
        draftingIsGenerating: false,
        draftingPendingDocumentId: null,
        draftingFocusRequestAt: Date.now(),
      }
    );
  }, [kernel, dealId, pendingGeneratedDocumentId, pendingGeneratedDocument]);

  const templatesForSelectedType = useMemo(
    () => templateCatalog.filter((template) => template.documentType === selectedDocumentType),
    [templateCatalog, selectedDocumentType]
  );

  useEffect(() => {
    if (templatesForSelectedType.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      setSelectedTemplateId(null);
      return;
    }
    if (selectedTemplateId && templatesForSelectedType.some((template) => template.id === selectedTemplateId)) {
      return;
    }

    const preferredTemplateIds = [
      selectedDocument?.templateId ?? null,
      selectedDocumentType === 'INCENTIVE' ? deal?.incentiveTemplateId ?? null : null,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    const preferred = preferredTemplateIds.find((id) =>
      templatesForSelectedType.some((template) => template.id === id)
    );
    setSelectedTemplateId(preferred ?? templatesForSelectedType[0]?.id ?? null);
  }, [
    templatesForSelectedType,
    selectedTemplateId,
    selectedDocument?.templateId,
    selectedDocumentType,
    deal?.incentiveTemplateId,
  ]);

  useEffect(() => {
    if (!selectedTemplateId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      setSelectedTemplate(null);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setIsTemplatePreviewLoading(true);
      try {
        const response = await fetch(
          `/api/inventory/document-templates/${encodeURIComponent(selectedTemplateId)}?dealId=${encodeURIComponent(dealId)}`,
          {
            method: 'GET',
            cache: 'no-store',
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error?.message || 'Failed to load template preview');
        }
        const payload = await response.json();
        if (cancelled) return;
        setSelectedTemplate(payload as DocumentTemplateDetail);
      } catch (error: any) {
        if (cancelled || error?.name === 'AbortError') return;
        toast.error(error?.message || 'Failed to load template preview');
        setSelectedTemplate(null);
      } finally {
        if (!cancelled) {
          setIsTemplatePreviewLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [dealId, selectedTemplateId]);

  const previewUrl = selectedDocument ? `/api/inventory/documents/${selectedDocument.id}/view` : null;
  const hasGenerateSuggestion = useMemo(
    () =>
      (selectedDocumentType === 'INCENTIVE' && highlightedActionTypes.includes('REDACT_INCENTIVE_LETTER')) ||
      (selectedDocumentType === 'LOI' && highlightedActionTypes.includes('REDACT_LOI')),
    [highlightedActionTypes, selectedDocumentType]
  );

  const handleGenerate = useCallback(async () => {
    if (!kernel.current || isGenerationBusy) return;
    if (!selectedTemplateId) {
      toast.error('Select a template before generating.');
      return;
    }
    setIsGenerating(true);

    try {
      const result = await kernel.current.generateDocument(dealId, selectedTemplateId, selectedDocumentType);
      const generatedDoc = result?.document as GeneratedDocument | undefined;
      if (generatedDoc?.id) {
        setSelectedDocumentType(generatedDoc.type);
      }
      toast.success(`${selectedDocumentType} document generated`);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to generate document');
    } finally {
      setIsGenerating(false);
    }
  }, [kernel, dealId, isGenerationBusy, selectedTemplateId, selectedDocumentType]);

  const handleSelectTemplate = useCallback(async (templateId: string) => {
    setSelectedTemplateId(templateId);
    setShowTemplateDropdown(false);

    if (!kernel.current || selectedDocumentType !== 'INCENTIVE') return;
    if (deal?.incentiveTemplateId === templateId) return;

    try {
      await kernel.current.mutateInventory(
        [
          {
            action: 'UPDATE',
            entity: 'Deal',
            id: dealId,
            payload: {
              incentiveTemplateId: templateId,
            },
          },
        ],
        'Select incentive template'
      );
    } catch (error: any) {
      toast.error(error?.message || 'Failed to set incentive template');
    }
  }, [kernel, deal?.incentiveTemplateId, selectedDocumentType, dealId]);

  const handleSelectDocument = useCallback((docId: string) => {
    setActivePreviewMode('DOCUMENT');
    setSelectedDocumentId(docId);
    setContextMenu(null);
    persistDraftingUiState({
      draftingPreviewMode: 'DOCUMENT',
      draftingSelectedDocumentType: selectedDocumentType,
      draftingSelectedDocumentId: docId,
    });
  }, [persistDraftingUiState, selectedDocumentType]);

  const [showVersionDropdown, setShowVersionDropdown] = useState(false);

  const handleSelectTemplatePreview = useCallback(() => {
    setActivePreviewMode('TEMPLATE');
    setShowVersionDropdown(false);
    setContextMenu(null);
    persistDraftingUiState({
      draftingPreviewMode: 'TEMPLATE',
      draftingSelectedDocumentType: selectedDocumentType,
      draftingSelectedDocumentId: selectedDocumentId ?? null,
    });
  }, [persistDraftingUiState, selectedDocumentType, selectedDocumentId]);

  const handleDocumentContextMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>, docId: string) => {
    event.preventDefault();
    setSelectedDocumentId(docId);
    setContextMenu({ docId, x: event.clientX, y: event.clientY });
  }, []);

  useEffect(() => {
    if (!contextMenu) return;

    const handleWindowClick = () => setContextMenu(null);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContextMenu(null);
    };

    window.addEventListener('click', handleWindowClick);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('click', handleWindowClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu]);

  const handleDeleteDocument = useCallback(async (docId: string) => {
    if (!kernel.current) return;
    const confirmed = window.confirm('Delete this generated document?');
    if (!confirmed) return;

    try {
      await kernel.current.deleteEntity('GeneratedDocument', docId);
      await kernel.current.syncEntity('Deal', dealId);

      setContextMenu(null);
      setSelectedDocumentId((current) => (current === docId ? null : current));
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete document');
    }
  }, [kernel, dealId]);

  const handleDownloadDocument = useCallback((docId: string) => {
    if (!kernel.current) return;
    void kernel.current.downloadGeneratedDocument(docId);
    setContextMenu(null);
  }, [kernel]);

  const handleSelectDocumentType = useCallback((type: GeneratedDocumentType) => {
    setSelectedDocumentType(type);

    const docsForType = [...(deal?.generatedDocs ?? [])]
      .filter((doc) => doc.type === type)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const latestDocId = docsForType[0]?.id ?? null;
    const nextPreviewMode: 'TEMPLATE' | 'DOCUMENT' = latestDocId ? 'DOCUMENT' : 'TEMPLATE';

    setActivePreviewMode(nextPreviewMode);
    setSelectedDocumentId(latestDocId);

    persistDraftingUiState({
      draftingPreviewMode: nextPreviewMode,
      draftingSelectedDocumentType: type,
      draftingSelectedDocumentId: latestDocId,
    });
  }, [deal?.generatedDocs, persistDraftingUiState]);


  const handleToggleTemplateDropdown = useCallback(() => setShowTemplateDropdown((prev) => !prev), []);
  const handleToggleVersionDropdown = useCallback(() => setShowVersionDropdown((prev) => !prev), []);

  const handleDocumentTypeClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const type = event.currentTarget.dataset.documentType;
    if (type === 'INCENTIVE' || type === 'LOI') handleSelectDocumentType(type);
  }, [handleSelectDocumentType]);

  const handleTemplateOptionClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const templateId = event.currentTarget.dataset.templateId;
    if (templateId) void handleSelectTemplate(templateId);
  }, [handleSelectTemplate]);

  const handleDocumentOptionClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const docId = event.currentTarget.dataset.docId;
    if (!docId) return;
    handleSelectDocument(docId);
    setShowVersionDropdown(false);
  }, [handleSelectDocument]);

  const handleDownloadSelectedDocument = useCallback(() => {
    if (selectedDocument) handleDownloadDocument(selectedDocument.id);
  }, [selectedDocument, handleDownloadDocument]);

  const handleDeleteSelectedDocument = useCallback(() => {
    if (selectedDocument) void handleDeleteDocument(selectedDocument.id);
  }, [selectedDocument, handleDeleteDocument]);

  const handleContextMenuDownload = useCallback(() => {
    if (contextMenu) handleDownloadDocument(contextMenu.docId);
  }, [contextMenu, handleDownloadDocument]);

  const handleContextMenuDelete = useCallback(() => {
    if (contextMenu) void handleDeleteDocument(contextMenu.docId);
  }, [contextMenu, handleDeleteDocument]);

  const contextMenuStyle = useMemo<React.CSSProperties | undefined>(
    () => (contextMenu ? { left: contextMenu.x, top: contextMenu.y } : undefined),
    [contextMenu]
  );

  const selectedTemplateMeta = useMemo(
    () => templatesForSelectedType.find((template) => template.id === selectedTemplateId) ?? null,
    [templatesForSelectedType, selectedTemplateId]
  );
  const hasTemplatePreview = Boolean(selectedTemplate?.htmlContent);
  const hasDocument = activePreviewMode === 'DOCUMENT' && Boolean(selectedDocument && previewUrl && isDocumentReady(selectedDocument));
  const isDocumentProcessing = activePreviewMode === 'DOCUMENT' && Boolean(selectedDocument) && !hasDocument;
  const showTemplateGeneratingOverlay = activePreviewMode === 'TEMPLATE' && (isGenerationBusy || isAwaitingGeneratedDocument);
  const stale = selectedDocument ? isDocumentStale(selectedDocument) : false;

  useEffect(() => {
    if (!showTemplateGeneratingOverlay || !templatePulseRef.current) return;

    const animation = anime({
      targets: templatePulseRef.current,
      backgroundColor: ['rgba(70, 70, 70, 0)', 'rgba(70, 70, 70, 0.35)'],
      duration: 620,
      direction: 'alternate',
      loop: true,
      easing: 'easeInOutSine',
    });

    return () => {
      animation.pause();
    };
  }, [showTemplateGeneratingOverlay]);

  if (!deal) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-white/30">
        Loading document workspace...
      </div>
    );
  }

  const TOOLBAR_BTN = 'inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-[11px] font-normal text-white/70 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40';
  const PREVIEW_STAGE = 'h-full overflow-auto bg-[#101113]';
  const PREVIEW_SHEET_FRAME =
    'h-[1120px] w-full max-w-[820px] rounded-[8px] border border-white/20 bg-white shadow-[0_28px_70px_rgba(0,0,0,0.55),0_3px_12px_rgba(0,0,0,0.45)]';

  return (
    <div className="flex h-full flex-col bg-black text-white">
      {/* ─── Toolbar ─── */}
      <div className="flex items-center justify-between gap-2 border-b border-[#2A2A2A] px-4 py-2 shrink-0">
        {/* Left: generate + selectors */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerationBusy}
            className={clsx('inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-white font-medium text-black transition-colors hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-50 px-2.5 py-1 text-[11px]', hasGenerateSuggestion && !isGenerationBusy && 'ring-1 ring-[#3B6FE0]')}
          >
            {isGenerationBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {isGenerationBusy ? 'Generating...' : 'Generate'}
          </button>

          <div className="h-4 w-px bg-white/10" />

          <div className="inline-flex items-center rounded-md border border-white/10 bg-white/5 p-0.5">
            {(['INCENTIVE', 'LOI'] as const).map((type) => {
              const isActive = type === selectedDocumentType;
              return (
                <button
                  key={type}
                  type="button"
                  data-document-type={type}
                  onClick={handleDocumentTypeClick}
                  className={clsx(
                    'rounded px-2 py-1 text-[10px] font-medium transition-colors',
                    isActive ? 'bg-white/15 text-white' : 'text-white/55 hover:text-white'
                  )}
                >
                  {type === 'INCENTIVE' ? 'Incentive Letter' : 'LOI'}
                </button>
              );
            })}
          </div>

          <div className="h-4 w-px bg-white/10" />

          {/* Template selector dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={handleToggleTemplateDropdown}
              className={clsx(TOOLBAR_BTN, 'min-w-[210px] justify-between')}
            >
              <div className="flex items-center gap-1.5 truncate">
                <Eye size={12} className="shrink-0 text-white/40" />
                <span className="truncate">
                  {selectedTemplateMeta
                    ? selectedTemplateMeta.name
                    : isTemplateCatalogLoading
                      ? 'Loading templates...'
                      : 'No template'}
                </span>
              </div>
              <ChevronDown size={12} className="shrink-0 text-white/30" />
            </button>
            {showTemplateDropdown && (
              <div className="absolute left-0 top-full z-50 mt-1 w-[340px] rounded-[16px] border border-white/[0.06] bg-[#353535] p-1.5 shadow-2xl">
                {templatesForSelectedType.length === 0 ? (
                  <div className="px-3 py-2 text-[11px] text-white/40">No templates available for {selectedDocumentType}.</div>
                ) : (
                  templatesForSelectedType.map((template) => {
                    const isActive = template.id === selectedTemplateId;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        data-template-id={template.id}
                        onClick={handleTemplateOptionClick}
                        className={clsx(
                          'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left transition-colors',
                          isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
                        )}
                      >
                        <div className="min-w-0">
                          <div className="truncate text-[12px]">{template.name}</div>
                          <div className="truncate text-[10px] text-white/35">
                            {template.description || (template.isGlobal ? 'Global template' : 'Organisation template')}
                          </div>
                        </div>
                        <span className={clsx(
                          'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-normal',
                          template.isGlobal ? 'bg-white/10 text-white/70' : 'bg-blue-500/20 text-blue-200'
                        )}>
                          {template.isGlobal ? 'global' : 'org'}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="h-4 w-px bg-white/10" />

          {/* Version selector dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={handleToggleVersionDropdown}
              className={clsx(TOOLBAR_BTN, 'min-w-[140px] justify-between')}
            >
              <div className="flex items-center gap-1.5 truncate">
                <FileText size={12} className="shrink-0 text-white/40" />
                <span className="truncate">
                  {activePreviewMode === 'TEMPLATE'
                    ? 'Template preview'
                    : selectedDocument
                      ? `${selectedDocument.templateName?.slice(0, 20) ?? 'Document'}${stale ? ' (stale)' : ''}`
                      : 'No document'}
                </span>
              </div>
              <ChevronDown size={12} className="shrink-0 text-white/30" />
            </button>
            {showVersionDropdown && (
              <div className="absolute left-0 top-full z-50 mt-1 w-[280px] rounded-[16px] border border-white/[0.06] bg-[#353535] p-1.5 shadow-2xl">
                <button
                  type="button"
                  onClick={handleSelectTemplatePreview}
                  className={clsx(
                    'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left transition-colors',
                    activePreviewMode === 'TEMPLATE'
                      ? 'bg-white/10 text-white'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  )}
                >
                  <div className="min-w-0">
                    <div className="truncate text-[12px]">Template preview</div>
                    <div className="text-[10px] text-white/30">View the selected template with placeholders</div>
                  </div>
                  <span className="shrink-0 rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-normal text-white/70">
                    template
                  </span>
                </button>

                {generatedDocs.length === 0 ? (
                  <div className="mt-1 border-t border-white/10 px-3 py-2 text-[11px] text-white/40">No documents generated yet.</div>
                ) : (
                  <>
                    <div className="my-1 border-t border-white/10" />
                    {generatedDocs.map((doc) => {
                      const isActive = activePreviewMode === 'DOCUMENT' && doc.id === selectedDocument?.id;
                      const docStale = isDocumentStale(doc);
                      const docReady = isDocumentReady(doc);
                      return (
                        <button
                          key={doc.id}
                          type="button"
                          data-doc-id={doc.id}
                          onClick={handleDocumentOptionClick}
                          className={clsx(
                            'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left transition-colors',
                            isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
                          )}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-[12px]">{doc.templateName}</div>
                            <div className="text-[10px] text-white/30">{new Date(doc.createdAt).toLocaleString()}</div>
                          </div>
                          <span className={clsx(
                            'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-normal',
                            docStale ? 'bg-rose-500/20 text-rose-200' : docReady ? 'bg-white/10 text-white/60' : 'bg-amber-500/20 text-amber-200'
                          )}>
                            {docStale ? 'stale' : doc.status?.toLowerCase()}
                          </span>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          {hasDocument && (
            <>
              <button
                type="button"
                onClick={handleDownloadSelectedDocument}
                className={TOOLBAR_BTN}
              >
                <Download size={13} />
                Export
              </button>
              <a
                href={previewUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className={TOOLBAR_BTN}
              >
                <ExternalLink size={13} />
                Open
              </a>
              <button
                type="button"
                onClick={handleDeleteSelectedDocument}
                className={clsx(TOOLBAR_BTN, 'text-rose-300/60 hover:text-rose-300 hover:bg-rose-500/10 hover:border-rose-500/20')}
              >
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ─── Document preview (full width) ─── */}
      <div className="flex-1 overflow-hidden">
        {hasDocument ? (
          <div className={PREVIEW_STAGE}>
            <div className="mx-auto flex min-h-full w-full items-start justify-center p-4 md:p-6">
              <iframe
                title="Document preview"
                src={previewUrl!}
                className={PREVIEW_SHEET_FRAME}
              />
            </div>
          </div>
        ) : isDocumentProcessing ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <Loader2 size={40} className="mx-auto animate-spin text-white/25" />
            </div>
            <div>
              <div className="text-[13px] text-white/70">Document is processing...</div>
              <p className="mt-1 max-w-md text-[11px] text-white/35">
                This generated document will appear here as soon as rendering completes.
              </p>
            </div>
          </div>
        ) : isTemplatePreviewLoading ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <Loader2 size={40} className="mx-auto animate-spin text-white/25" />
            </div>
            <div>
              <div className="text-[13px] text-white/70">Loading template preview...</div>
              <p className="mt-1 max-w-md text-[11px] text-white/35">
                The selected template structure with placeholders will render here.
              </p>
            </div>
          </div>
        ) : hasTemplatePreview ? (
          <div className={clsx(PREVIEW_STAGE, 'relative')}>
            <div className="mx-auto flex min-h-full w-full items-start justify-center p-4 md:p-6">
              <div className={clsx(PREVIEW_SHEET_FRAME, 'relative overflow-hidden')}>
                <iframe
                  title="Template preview"
                  srcDoc={selectedTemplate?.htmlContent ?? ''}
                  sandbox="allow-same-origin allow-scripts"
                  className="h-full w-full rounded-[8px] bg-white"
                />
                {showTemplateGeneratingOverlay && (
                  <div ref={templatePulseRef} className="pointer-events-none absolute inset-0 rounded-[8px]" />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <FileText size={40} className="mx-auto text-white/15" />
            </div>
            <div>
              <div className="text-[13px] text-white/70">
                {templatesForSelectedType.length > 0 ? 'No document yet' : 'No template available'}
              </div>
              <p className="mt-1 max-w-md text-[11px] text-white/35">
                {templatesForSelectedType.length > 0
                  ? 'Select a template and click Generate. The final document will render here.'
                  : `Create or import a ${selectedDocumentType} template to unlock generation.`}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ─── Context menu (right-click on version dropdown items) ─── */}
      {contextMenu && (
        <div
          className="fixed z-[1200] min-w-[200px] rounded-[16px] border border-white/[0.06] bg-[#353535] p-1.5 shadow-2xl"
          style={contextMenuStyle}
          onClick={stopEventPropagation}
          onContextMenu={preventEventDefault}
        >
          <button
            type="button"
            onClick={handleContextMenuDownload}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-[11px] font-normal text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Download size={13} />
            Download
          </button>
          <button
            type="button"
            onClick={handleContextMenuDelete}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-[11px] font-normal text-rose-200/80 transition-colors hover:bg-rose-500/15 hover:text-rose-100"
          >
            <Trash2 size={13} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}






